import React, { useEffect, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Modal from '@cloudscape-design/components/modal';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Alert from '@cloudscape-design/components/alert';
import { getSupplierAdminService } from '../services';
import type { SupplierMember, SupplierEntity } from '../services/types';

/**
 * Supplier Users — a supplier ADMIN adds and manages users without an admin
 * round-trip. Access is group-level: every user in the supplier group inherits
 * the SAME legal entities (set by the Supplier Gateway admin at approval), so
 * there is no per-user entity grant. The admin only adds/disables users; the
 * group's entity set is shown read-only as a summary. Users share the
 * supplier's one API credential.
 */
export default function SupplierUsersScreen() {
    const [members, setMembers] = useState<SupplierMember[]>([]);
    const [entities, setEntities] = useState<SupplierEntity[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviting, setInviting] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);

    const [busyUser, setBusyUser] = useState<string | null>(null);

    const refresh = () => {
        setLoading(true);
        setLoadError(null);
        Promise.all([
            getSupplierAdminService().listMembers(),
            getSupplierAdminService().listEntities(),
        ])
            .then(([ms, ents]) => {
                setMembers(ms);
                setEntities(ents);
            })
            .catch((e) => setLoadError(e instanceof Error ? e.message : 'Failed to load.'))
            .finally(() => setLoading(false));
    };

    useEffect(refresh, []);

    const submitInvite = () => {
        const email = inviteEmail.trim();
        if (!email) return;
        setInviting(true);
        setInviteError(null);
        getSupplierAdminService()
            .inviteMember(email)
            .then(() => {
                setInviteOpen(false);
                setInviteEmail('');
                refresh();
            })
            .catch((e) =>
                setInviteError(e instanceof Error ? e.message : 'Failed to invite member.'),
            )
            .finally(() => setInviting(false));
    };

    const toggle = (m: SupplierMember) => {
        setBusyUser(m.username);
        const svc = getSupplierAdminService();
        const action = m.enabled ? svc.disableMember(m.username) : svc.enableMember(m.username);
        action.then(refresh).finally(() => setBusyUser(null));
    };

    return (
        <SpaceBetween size="l">
            {loadError ? <Alert type="error">{loadError}</Alert> : null}

            {/* Group entity access summary — read-only. Every user below has
                access to all of these; access is set by the admin at approval. */}
            <Table
                variant="container"
                loading={loading}
                items={entities}
                header={
                    <Header
                        counter={`(${entities.length})`}
                        description="Every user in your supplier has access to all of these legal entities."
                    >
                        Legal entity access
                    </Header>
                }
                empty={
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        No legal entities assigned to your supplier yet.
                    </Box>
                }
                columnDefinitions={[
                    {
                        id: 'supplierNo',
                        header: 'SupplierNo',
                        cell: (e) => <Box fontWeight="bold">{e.supplierNo}</Box>,
                    },
                    { id: 'label', header: 'Legal entity', cell: (e) => e.displayLabel },
                ]}
            />

            <Table
                variant="container"
                loading={loading}
                items={members}
                header={
                    <Header
                        counter={`(${members.length})`}
                        actions={
                            <Button variant="primary" onClick={() => setInviteOpen(true)}>
                                Invite user
                            </Button>
                        }
                    >
                        Supplier users
                    </Header>
                }
                empty={
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        No users yet. Invite your first one.
                    </Box>
                }
                columnDefinitions={[
                    {
                        id: 'email',
                        header: 'User',
                        cell: (m) => <Box fontWeight="bold">{m.email || m.username}</Box>,
                    },
                    {
                        id: 'role',
                        header: 'Role',
                        cell: (m) => (
                            <Badge color={m.role === 'MASTER' ? 'green' : 'blue'}>
                                {m.role === 'MASTER' ? 'Admin' : 'User'}
                            </Badge>
                        ),
                    },
                    {
                        id: 'status',
                        header: 'Status',
                        cell: (m) => (
                            <StatusIndicator type={m.enabled ? 'success' : 'stopped'}>
                                {m.enabled ? m.status || 'Active' : 'Disabled'}
                            </StatusIndicator>
                        ),
                    },
                    {
                        id: 'actions',
                        header: '',
                        // The admin owns the group; only non-admin users can be
                        // enabled/disabled. Access is group-level, so there is no
                        // per-user entity management here.
                        cell: (m) =>
                            m.role === 'MASTER' ? (
                                <Box color="text-body-secondary">Admin</Box>
                            ) : (
                                <Button
                                    variant="normal"
                                    loading={busyUser === m.username}
                                    disabled={busyUser !== null}
                                    onClick={() => toggle(m)}
                                >
                                    {m.enabled ? 'Disable' : 'Enable'}
                                </Button>
                            ),
                    },
                ]}
            />

            {inviteOpen ? (
                <Modal
                    visible
                    onDismiss={() => setInviteOpen(false)}
                    header="Invite a user"
                    footer={
                        <Box float="right">
                            <SpaceBetween size="xs" direction="horizontal">
                                <Button
                                    variant="link"
                                    onClick={() => setInviteOpen(false)}
                                    disabled={inviting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={submitInvite}
                                    loading={inviting}
                                    disabled={inviteEmail.trim().length === 0}
                                >
                                    Send invite
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween size="m">
                        <Box color="text-body-secondary">
                            The user must use an email on your supplier&apos;s domain. They&apos;ll
                            get an email with a temporary password, share your supplier&apos;s API
                            credential, and get access to the same legal entities.
                        </Box>
                        {inviteError ? <Alert type="error">{inviteError}</Alert> : null}
                        <FormField label="User email">
                            <Input
                                value={inviteEmail}
                                placeholder="user@yoursupplier.com"
                                type="email"
                                onChange={({ detail }) => setInviteEmail(detail.value)}
                            />
                        </FormField>
                    </SpaceBetween>
                </Modal>
            ) : null}
        </SpaceBetween>
    );
}
